import { Injectable, Inject, OnDestroy, Renderer2, RendererFactory2, DOCUMENT } from '@angular/core';
import { ResponsiveService } from './responsive.service';

import { ISidebarComponent } from 'src/app/shared/interfaces/sidebar-component.interface';
import { ProfileService } from '@api/api-services/profile.service';
import { BehaviorSubject, fromEvent, Subscription } from 'rxjs';
import { SubnavState } from '@app/shared/enums/subnav-state.enum';

@Injectable()
export class LayoutService implements OnDestroy {
  public isSidebarOpen = false;
  public focusAfterProgramChange = false;

  private readonly _subnavState$ = new BehaviorSubject<SubnavState>(SubnavState.none);
  public subnavState$ = this._subnavState$.asObservable();

  private readonly _showProgramSwitcher$ = new BehaviorSubject<boolean>(false);
  public showProgramSwitcher$ = this._showProgramSwitcher$.asObservable();

  public barsDisabled = false;
  public sidebarComponent: ISidebarComponent;
  public forceOpenSubNav: boolean;

  private _mainView: HTMLElement;

  public get mainView(): HTMLElement {
    if (!this._mainView) {
      this._mainView = this.document.getElementById('content');
    }
    return this._mainView;
  }

  public isMobileScreen = window.innerWidth < 768;

  private readonly resizeSubscription: Subscription;
  private readonly subnavStateSubscription: Subscription;
  private pendingToggleTimers: number[] = [];

  private readonly renderer: Renderer2;

  constructor(
    private readonly responsiveService: ResponsiveService,
    private readonly profileService: ProfileService,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly rendererFactory: RendererFactory2
  ) {
    this.isSidebarOpen = this.responsiveService.isDesktopWidth();
    this.renderer = this.rendererFactory.createRenderer(null, null);

    this.subnavStateSubscription = this._subnavState$.subscribe(() => {
      this.adjustLayoutForSubnav();
    });

    this.resizeSubscription = fromEvent(window, 'resize').subscribe(() => {
      const wasMobile = this.isMobileScreen;
      this.isMobileScreen = window.innerWidth < 768;
      if (!this.isMobileScreen && wasMobile) {
        if (this.subnavState === SubnavState.full) {
          this.subnavState = window.innerWidth < 1024 ? SubnavState.collapsed : SubnavState.full;
        }
      }
    });
  }

  public get subnavState(): SubnavState {
    return this._subnavState$.value;
  }
  public set subnavState(val: SubnavState) {
    if (this._subnavState$.value !== val) {
      this._subnavState$.next(val);
    }
  }

  public get showProgramSwitcher(): boolean {
    return this._showProgramSwitcher$.value;
  }
  public set showProgramSwitcher(val: boolean) {
    if (this._showProgramSwitcher$.value !== val) {
      this._showProgramSwitcher$.next(val);
    }
  }

  public openSidebar(): void {
    this.isSidebarOpen = true;
  }

  public toggleSidebar(): void {
    this.isSidebarOpen = !this.isSidebarOpen;
  }

  public closeSidebar(): void {
    this.isSidebarOpen = false;
  }

  public scrollToBottom(): void {
    setTimeout(() => (this.mainView.scrollTop = this.mainView.scrollHeight), 0);
  }

  public scrollToTop(): void {
    setTimeout(() => (this.mainView.scrollTop = 0), 0);
  }

  public scrollToBottomOfElem(elem: HTMLElement): void {
    elem.scrollTop = elem.scrollHeight;
  }

  public get yScrollPosition(): number {
    return this.mainView.scrollTop;
  }

  public set yScrollPosition(value: number) {
    this.mainView.scrollTop = value;
  }

  private toggleClassOnElements(selector: string, className: string, enable: boolean): void {
    this.pendingToggleTimers.forEach((timer) => clearTimeout(timer));
    this.pendingToggleTimers = [];

    const tryToggle = (attempt: number) => {
      const elements = document.querySelectorAll(selector);
      if (elements.length === 0 && attempt < 20) {
        const timer = window.setTimeout(() => tryToggle(attempt + 1), 200);
        this.pendingToggleTimers.push(timer);
        return;
      }
      this.pendingToggleTimers = [];

      elements.forEach((element) => {
        if (enable) {
          element.classList.add(className);
        } else {
          element.classList.remove(className);
        }
      });
    };
    tryToggle(0);
  }

  public enableNightMode(persist = false): void {
    if (!persist) {
      this.toggleClassOnElements('main, a360-navbar,top-sub-nav,a360-subnav,program-switcher', 'night-mode', true);
      this.renderer.addClass(document.body, 'dark-theme');
      return;
    }
    this.profileService.setThemePreference(true).subscribe(() => {
      this.toggleClassOnElements('main, a360-navbar,top-sub-nav,a360-subnav,program-switcher', 'night-mode', true);
      this.renderer.addClass(document.body, 'dark-theme');
    });
  }

  public enableDayMode(persist = false): void {
    if (!persist) {
      this.toggleClassOnElements('main, a360-navbar,top-sub-nav,a360-subnav,program-switcher', 'night-mode', false);
      this.renderer.removeClass(document.body, 'dark-theme');
      return;
    }
    this.profileService.setThemePreference(false).subscribe(() => {
      this.toggleClassOnElements('main, a360-navbar,top-sub-nav,a360-subnav,program-switcher', 'night-mode', false);
      this.renderer.removeClass(document.body, 'dark-theme');
    });
  }

  public adjustLayoutForSubnav(): void {
    const isMobile = window.innerWidth < 768;

    const main = document.getElementById('content');
    if (main) {
      if (this.subnavState === SubnavState.hidden || isMobile) {
        main.style.paddingLeft = '0px';
      } else {
        main.style.paddingLeft = this.subnavState === SubnavState.collapsed ? '60px' : '263px';
      }
    }
    const topSubNav = document.querySelector('.top-sub-nav') as HTMLElement;
    if (topSubNav) {
      if (this.subnavState === SubnavState.hidden || isMobile) {
        topSubNav.style.marginLeft = '0px';
      } else {
        topSubNav.style.marginLeft = this.subnavState === SubnavState.collapsed ? '50px' : '248px';
      }
    }
  }

  public clearMainMargin(): void {
    const main = document.getElementById('content');
    if (main) {
      main.style.paddingLeft = '0px';
    }
  }
  adjustLayoutForLegacySidebar(): void {
    const main = document.getElementById('content');
    const isMobile = window.innerWidth < 768;
    if (main) {
      if (isMobile) {
        main.style.paddingLeft = '0px';
      } else {
        main.style.paddingLeft = '215px';
      }
    }
  }

  ngOnDestroy(): void {
    if (this.resizeSubscription) {
      this.resizeSubscription.unsubscribe();
    }
    if (this.subnavStateSubscription) {
      this.subnavStateSubscription.unsubscribe();
    }
    this.pendingToggleTimers.forEach((timer) => clearTimeout(timer));
    this.pendingToggleTimers = [];
  }
}
